import { useCallback, useEffect, useState } from "react";

import { OverviewVersionBrowser } from "./OverviewVersionBrowser";
import { SkillCollectionVersionSelector } from "./SkillCollectionVersionSelector";

import useIsMobile from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";

interface SkillCollectionOverviewProps {
  workspaceId: number;
  collectionId: string;
  rootPath: string;
  selectedVersionNumber: number | null;
  selectedVersionFilePaths: string[];
  initialSelectedPath?: string | null;
  onSelectedPathChange?: (path: string | null) => void;
}

const SMALL_SCREEN_BREAKPOINT = 1600;
const DEFAULT_SHOW_VERSION_LIST = true;

const isSmallScreenViewport = () =>
  typeof window !== "undefined" && window.innerWidth <= SMALL_SCREEN_BREAKPOINT;

const SkillCollectionOverview = ({
  workspaceId,
  collectionId,
  rootPath,
  selectedVersionNumber,
  selectedVersionFilePaths,
  initialSelectedPath,
  onSelectedPathChange,
}: SkillCollectionOverviewProps) => {
  const isSmallScreen = useIsMobile(SMALL_SCREEN_BREAKPOINT);
  const [desktopShowVersionList, setDesktopShowVersionList] = useState(DEFAULT_SHOW_VERSION_LIST);
  const [showVersionList, setShowVersionList] = useState(
    () => !isSmallScreenViewport() && DEFAULT_SHOW_VERSION_LIST,
  );

  useEffect(() => {
    setShowVersionList(isSmallScreen ? false : desktopShowVersionList);
  }, [desktopShowVersionList, isSmallScreen]);

  const handleSetShowVersionList = useCallback(
    (value: boolean) => {
      setShowVersionList(value);
      if (!isSmallScreen) {
        setDesktopShowVersionList(value);
      }
    },
    [isSmallScreen],
  );

  return (
    <div className="flex h-full min-h-0 flex-row gap-x-2 overflow-hidden py-4">
      <div
        className={cn(
          "min-h-0 min-w-0 shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out",
          showVersionList ? "w-[360px]" : "w-12",
        )}
      >
        <SkillCollectionVersionSelector
          workspaceId={workspaceId}
          collectionId={collectionId}
          showVersionList={showVersionList}
          setShowVersionList={handleSetShowVersionList}
          className="h-full w-full min-w-0 max-w-none"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="h-full min-h-0">
          <OverviewVersionBrowser
            workspaceId={workspaceId}
            collectionId={collectionId}
            rootPath={rootPath}
            versionNumber={selectedVersionNumber}
            filePaths={selectedVersionFilePaths}
            initialSelectedPath={initialSelectedPath}
            onSelectedPathChange={onSelectedPathChange}
          />
        </div>
      </div>
    </div>
  );
};

export default SkillCollectionOverview;
